import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Group, Layer, Rect, Stage, Text, Transformer } from 'react-konva';
import { API_BASE, apiFetch } from './api';
import { mergeStyles, theme } from './theme';

const DEFAULT_STAGE = { width: 1400, height: 800 };
const LOOKUP_CACHE_KEY = 'dispoboard_warehouse_lookup_cache_v1';
const AUTO_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const ACTIVE_USAGE_WINDOW_MS = 5 * 60 * 1000;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

const styles = {
  stack: { display: 'grid', gap: '16px' },
  tabRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  tabButton: { ...theme.button, ...theme.mutedButton },
  tabButtonActive: { ...theme.button, ...theme.primaryButton },
  tabActions: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginLeft: 'auto' },
  page: { display: 'grid', gridTemplateColumns: '250px minmax(0, 1fr) 340px', gap: '16px', alignItems: 'start' },
  panel: { ...theme.glassCard, padding: '18px', color: 'var(--board-text-strong, #fff)', boxSizing: 'border-box' },
  canvasPanel: { ...theme.glassCard, padding: '12px', color: 'var(--board-text-strong, #fff)', overflow: 'auto', boxSizing: 'border-box' },
  title: { margin: '0 0 12px', fontSize: '18px', fontWeight: 900 },
  input: { ...theme.input, width: '100%', marginBottom: '10px' },
  select: { ...theme.select, width: '100%', marginBottom: '10px', colorScheme: 'var(--board-color-scheme, dark)' },
  label: { display: 'block', marginBottom: '5px', fontSize: '12px', color: 'var(--board-text-muted, rgba(255,255,255,0.62))' },
  button: { ...theme.button, ...theme.mutedButton, width: '100%', marginBottom: '8px', borderRadius: '6px' },
  primaryButton: { ...theme.button, ...theme.primaryButton, width: '100%', borderRadius: '6px' },
  dangerButton: { ...theme.button, ...theme.dangerButton, width: '100%', borderRadius: '6px' },
  inlineButton: { ...theme.button, ...theme.mutedButton, borderRadius: '6px' },
  muted: { color: 'var(--board-text-muted, rgba(255,255,255,0.62))', fontSize: '13px', lineHeight: 1.4 },
  strong: { color: 'var(--board-text-strong, #fff)', fontWeight: 800 },
  status: { minHeight: '18px', color: 'var(--board-text-soft, rgba(255,255,255,0.72))', fontSize: '12px' },
  stageShell: { display: 'inline-block', background: 'var(--board-surface-soft, #0a1020)', border: '1px solid var(--board-border, rgba(255,255,255,0.18))' },
  layoutCard: {
    padding: '10px 12px',
    borderRadius: '8px',
    background: 'var(--board-surface-soft, rgba(255,255,255,0.06))',
    border: '1px solid var(--board-border, rgba(255,255,255,0.10))',
    marginBottom: '8px',
    cursor: 'pointer',
  },
  layoutCardActive: {
    padding: '10px 12px',
    borderRadius: '8px',
    background: 'rgba(59,130,246,0.18)',
    border: '1px solid rgba(147,197,253,0.54)',
    marginBottom: '8px',
    cursor: 'pointer',
  },
  shelfCard: {
    padding: '10px 12px',
    borderRadius: '8px',
    background: 'var(--board-surface-soft, rgba(255,255,255,0.06))',
    border: '1px solid var(--board-border, rgba(255,255,255,0.10))',
    marginBottom: '8px',
    cursor: 'grab',
    transition: 'transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 160ms ease, filter 160ms ease, opacity 160ms ease',
    willChange: 'transform',
  },
  shelfCardActive: {
    padding: '10px 12px',
    borderRadius: '8px',
    background: 'rgba(34,197,94,0.18)',
    border: '1px solid rgba(74,222,128,0.54)',
    marginBottom: '8px',
    cursor: 'grab',
    transition: 'transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 160ms ease, filter 160ms ease, opacity 160ms ease',
    willChange: 'transform',
  },
  badge: {
    display: 'inline-block',
    marginTop: '6px',
    marginRight: '6px',
    padding: '4px 8px',
    borderRadius: '999px',
    background: 'var(--board-surface-soft, rgba(255,255,255,0.08))',
    border: '1px solid var(--board-border, rgba(255,255,255,0.14))',
    fontSize: '11px',
  },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  lookupLayout: { display: 'grid', gridTemplateColumns: '340px minmax(0, 1fr)', gap: '16px', alignItems: 'start' },
  results: { display: 'grid', gap: '8px', marginTop: '12px' },
  resultButton: {
    ...theme.button,
    ...theme.mutedButton,
    width: '100%',
    textAlign: 'left',
    borderRadius: '8px',
    padding: '12px',
  },
  resultButtonActive: {
    ...theme.button,
    width: '100%',
    textAlign: 'left',
    borderRadius: '8px',
    padding: '12px',
    background: 'rgba(59,130,246,0.18)',
    border: '1px solid rgba(147,197,253,0.54)',
    color: 'var(--board-text-strong, #fff)',
  },
  viewerShell: { ...theme.glassCard, padding: '16px', color: 'var(--board-text-strong, #fff)' },
  rackWrap: { display: 'grid', gap: '10px' },
  rackGrid: { display: 'grid', gap: '4px' },
  rackRow: { display: 'grid', gap: '4px' },
  rackCell: {
    minHeight: '48px',
    borderRadius: '6px',
    background: 'var(--board-surface-soft, rgba(255,255,255,0.06))',
    border: '1px solid var(--board-border, rgba(255,255,255,0.1))',
    display: 'grid',
    placeItems: 'center',
    fontSize: '12px',
    fontWeight: 700,
  },
  rackCellHighlight: {
    minHeight: '48px',
    borderRadius: '6px',
    background: 'rgba(34,197,94,0.20)',
    border: '1px solid rgba(74,222,128,0.62)',
    display: 'grid',
    placeItems: 'center',
    fontSize: '12px',
    fontWeight: 800,
    color: 'var(--board-success-text, #f0fdf4)',
  },
};

function normalizeShelf(shelf) {
  return {
    shelfKey: String(shelf?.shelfKey || shelf?.shelfCode || '').trim().toUpperCase(),
    shelfCode: String(shelf?.shelfCode || shelf?.shelfKey || '').trim().toUpperCase(),
    areaPath: String(shelf?.areaPath || '').trim().toUpperCase(),
    displayName: String(shelf?.displayName || shelf?.shelfKey || shelf?.shelfCode || '').trim(),
    columns: Array.isArray(shelf?.columns) ? shelf.columns.map(value => String(value).trim().toUpperCase()).filter(Boolean) : [],
    columnStart: shelf?.columnStart === 'right' ? 'right' : 'left',
    levels: Array.isArray(shelf?.levels) ? shelf.levels.map(value => String(value).trim()).filter(Boolean) : [],
    levelStart: shelf?.levelStart === 'top' ? 'top' : 'bottom',
    note: String(shelf?.note || ''),
    articleCount: Number(shelf?.articleCount) || 0,
  };
}

function normalizePlacement(item) {
  return {
    id: String(item?.id || `shelf-${item?.shelfKey || item?.shelfCode || ''}`),
    shelfKey: String(item?.shelfKey || item?.shelfCode || '').trim().toUpperCase(),
    x: Number(item?.x) || 120,
    y: Number(item?.y) || 120,
    width: Math.max(80, Number(item?.width) || 180),
    height: Math.max(80, Number(item?.height) || 120),
    rotation: Number(item?.rotation) || 0,
  };
}

function normalizeLayout(layout, index = 0) {
  return {
    id: String(layout?.id || `layout-${index + 1}`),
    name: String(layout?.name || `Layout ${index + 1}`).trim() || `Layout ${index + 1}`,
    assignedTerms: Array.isArray(layout?.assignedTerms) ? layout.assignedTerms.map(value => String(value).trim().toUpperCase()).filter(Boolean) : [],
    isDefault: layout?.isDefault === true,
    stage: {
      width: Number(layout?.stage?.width) || DEFAULT_STAGE.width,
      height: Number(layout?.stage?.height) || DEFAULT_STAGE.height,
    },
    shelves: Array.isArray(layout?.shelves) ? layout.shelves.map(normalizeShelf) : [],
    placedShelves: Array.isArray(layout?.placedShelves) ? layout.placedShelves.map(normalizePlacement) : [],
  };
}

function normalizeDetectedLayout(item, index = 0) {
  return {
    layoutHint: String(item?.layoutHint || `DETECTED-${index + 1}`).trim().toUpperCase(),
    suggestedName: String(item?.suggestedName || item?.layoutHint || `Layout ${index + 1}`).trim(),
    assignedTerms: Array.isArray(item?.assignedTerms) ? item.assignedTerms.map(value => String(value).trim().toUpperCase()).filter(Boolean) : [],
    itemCount: Number(item?.itemCount) || 0,
    shelves: Array.isArray(item?.shelves) ? item.shelves.map(normalizeShelf) : [],
  };
}

function normalizeLookupItem(item) {
  return {
    ...item,
    id: String(item?.id || item?.rentman_id || ''),
    rentman_id: String(item?.rentman_id || ''),
    article_name: String(item?.article_name || '').trim(),
    storage_location: String(item?.storage_location || '').trim(),
    layoutId: item?.layoutId ? String(item.layoutId) : null,
    layoutName: item?.layoutName ? String(item.layoutName) : null,
    parsedLocation: item?.parsedLocation || null,
  };
}

function normalizeSetupPayload(payload) {
  return {
    layouts: Array.isArray(payload?.layouts) ? payload.layouts.map(normalizeLayout) : [],
    detectedLayouts: Array.isArray(payload?.detectedLayouts) ? payload.detectedLayouts.map(normalizeDetectedLayout) : [],
    items: Array.isArray(payload?.items) ? payload.items.map(normalizeLookupItem).filter(item => item.parsedLocation) : [],
    usingMockData: Boolean(payload?.usingMockData),
    itemCount: Number(payload?.itemCount) || 0,
    updatedAt: payload?.updatedAt || null,
    cacheUpdatedAt: payload?.cacheUpdatedAt || null,
    syncError: String(payload?.syncError || ''),
    source: String(payload?.source || ''),
  };
}

function readLookupCache() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(LOOKUP_CACHE_KEY));
    return parsed && typeof parsed === 'object' ? normalizeSetupPayload(parsed) : null;
  } catch {
    return null;
  }
}

function writeLookupCache(payload) {
  try {
    window.localStorage.setItem(LOOKUP_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore cache write failures
  }
}

function displayColumns(shelf) {
  const columns = Array.isArray(shelf?.columns) ? shelf.columns : [];
  return shelf?.columnStart === 'right' ? [...columns].reverse() : columns;
}

function displayLevelsTopDown(shelf) {
  const levels = Array.isArray(shelf?.levels) ? shelf.levels : [];
  return shelf?.levelStart === 'top' ? levels : [...levels].reverse();
}

function layoutTermsLabel(layout) {
  return layout.assignedTerms.length > 0 ? layout.assignedTerms.join(', ') : 'ohne festen Begriff';
}

function shelfLocationLabel(result) {
  return result?.parsedLocation?.normalized || result?.storage_location || '-';
}

function findDetectedLayoutForTerms(layout, detectedLayouts) {
  if (layout.assignedTerms.length > 0) {
    const matches = detectedLayouts.filter(item => item.assignedTerms.some(term => layout.assignedTerms.includes(term)));
    if (matches.length > 0) return matches;
  }
  if (layout.isDefault) {
    const fallback = detectedLayouts.filter(item => item.layoutHint === 'DEFAULT');
    if (fallback.length > 0) return fallback;
  }
  return [];
}

function getLayoutShelfMap(layout) {
  const map = new Map();
  layout.shelves.forEach(shelf => map.set(shelf.shelfKey, normalizeShelf(shelf)));
  return map;
}

function combineShelves(layout, detectedLayouts) {
  const map = getLayoutShelfMap(layout);
  findDetectedLayoutForTerms(layout, detectedLayouts).forEach(group => {
    group.shelves.forEach(shelf => {
      if (!map.has(shelf.shelfKey)) {
        map.set(shelf.shelfKey, normalizeShelf(shelf));
      } else {
        map.set(shelf.shelfKey, {
          ...normalizeShelf(shelf),
          ...map.get(shelf.shelfKey),
        });
      }
    });
  });
  return [...map.values()].sort((a, b) => a.shelfKey.localeCompare(b.shelfKey, 'de', { numeric: true }));
}

function LabeledInput({ label, value, onChange, type = 'text', min }) {
  return (
    <label>
      <span style={styles.label}>{label}</span>
      <input
        style={styles.input}
        type={type}
        min={min}
        value={value ?? ''}
        onChange={event => onChange(type === 'number' ? Number(event.target.value) || 0 : event.target.value)}
      />
    </label>
  );
}

function LayoutCanvas({ stage, placements, selectedShelfKey, onSelectShelf, onUpdatePlacement, onCreatePlacement, shelfLabelMap }) {
  const transformerRef = useRef(null);
  const shapeRefs = useRef({});
  const shellRef = useRef(null);
  const dragAnimationRef = useRef({});

  useEffect(() => {
    const transformer = transformerRef.current;
    const node = selectedShelfKey ? shapeRefs.current[selectedShelfKey] : null;
    transformer?.nodes(node ? [node] : []);
    transformer?.getLayer()?.batchDraw();
  }, [selectedShelfKey, placements]);

  const handleDrop = event => {
    event.preventDefault();
    const shelfKey = event.dataTransfer.getData('text/plain');
    if (!shelfKey) return;
    const bounds = shellRef.current?.getBoundingClientRect();
    if (!bounds) return;
    onCreatePlacement(shelfKey, {
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    });
  };

  const setStageCursor = (node, cursor) => {
    const container = node?.getStage()?.container();
    if (container) container.style.cursor = cursor;
  };

  const startPlacementDragAnimation = (placement, node) => {
    dragAnimationRef.current[placement.shelfKey] = {
      lastX: node.x(),
      rotation: placement.rotation || 0,
    };
    setStageCursor(node, 'grabbing');
    node.to({
      scaleX: 1.025,
      scaleY: 1.025,
      shadowColor: '#000',
      shadowBlur: 18,
      shadowOpacity: 0.32,
      duration: 0.08,
    });
  };

  const updatePlacementDragAnimation = (placement, node) => {
    const animation = dragAnimationRef.current[placement.shelfKey];
    if (!animation) return;

    const movementX = node.x() - animation.lastX;
    animation.lastX = node.x();
    node.rotation((placement.rotation || 0) + clamp(movementX * 0.12, -2.4, 2.4));
    node.getLayer()?.batchDraw();
  };

  const finishPlacementDragAnimation = (placement, node) => {
    delete dragAnimationRef.current[placement.shelfKey];
    setStageCursor(node, 'grab');
    node.to({
      scaleX: 1,
      scaleY: 1,
      rotation: placement.rotation || 0,
      shadowBlur: 0,
      shadowOpacity: 0,
      duration: 0.16,
    });
  };

  return (
    <main style={styles.canvasPanel}>
      <div ref={shellRef} style={styles.stageShell} onDragOver={event => event.preventDefault()} onDrop={handleDrop}>
        <Stage width={stage.width} height={stage.height} onMouseDown={event => {
          if (event.target === event.target.getStage() || event.target.name() === 'background') onSelectShelf('');
        }}>
          <Layer>
            <Rect name="background" width={stage.width} height={stage.height} fill="#09101d" />
            {Array.from({ length: Math.floor(stage.width / 50) + 1 }).map((_, index) => (
              <Rect key={`v-${index}`} x={index * 50} y={0} width={1} height={stage.height} fill="rgba(255,255,255,0.05)" listening={false} />
            ))}
            {Array.from({ length: Math.floor(stage.height / 50) + 1 }).map((_, index) => (
              <Rect key={`h-${index}`} x={0} y={index * 50} width={stage.width} height={1} fill="rgba(255,255,255,0.05)" listening={false} />
            ))}
            {placements.map(placement => {
              const selected = selectedShelfKey === placement.shelfKey;
              return (
                <Group
                  key={placement.shelfKey}
                  ref={node => {
                    if (node) shapeRefs.current[placement.shelfKey] = node;
                    else delete shapeRefs.current[placement.shelfKey];
                  }}
                  x={placement.x}
                  y={placement.y}
                  rotation={placement.rotation || 0}
                  draggable
                  onMouseEnter={event => setStageCursor(event.target, 'grab')}
                  onMouseLeave={event => setStageCursor(event.target, 'default')}
                  onClick={() => onSelectShelf(placement.shelfKey)}
                  onTap={() => onSelectShelf(placement.shelfKey)}
                  onDragStart={event => startPlacementDragAnimation(placement, event.target)}
                  onDragMove={event => updatePlacementDragAnimation(placement, event.target)}
                  onDragEnd={event => {
                    onUpdatePlacement(placement.shelfKey, { x: event.target.x(), y: event.target.y() });
                    finishPlacementDragAnimation(placement, event.target);
                  }}
                  onTransformEnd={event => {
                    const node = event.target;
                    const scaleX = node.scaleX();
                    const scaleY = node.scaleY();
                    node.scaleX(1);
                    node.scaleY(1);
                    onUpdatePlacement(placement.shelfKey, {
                      x: node.x(),
                      y: node.y(),
                      rotation: node.rotation(),
                      width: Math.max(80, placement.width * scaleX),
                      height: Math.max(80, placement.height * scaleY),
                    });
                  }}
                >
                  <Rect
                    width={placement.width}
                    height={placement.height}
                    fill={selected ? 'rgba(59,130,246,0.28)' : 'rgba(30,41,59,0.82)'}
                    stroke={selected ? '#93c5fd' : 'rgba(255,255,255,0.42)'}
                    strokeWidth={selected ? 3 : 2}
                    cornerRadius={8}
                  />
                  <Text
                    text={shelfLabelMap.get(placement.shelfKey) || placement.shelfKey}
                    width={placement.width}
                    height={placement.height}
                    align="center"
                    verticalAlign="middle"
                    fill="#fff"
                    fontStyle="bold"
                    fontSize={Math.max(18, Math.min(24, placement.height * 0.22))}
                    listening={false}
                  />
                </Group>
              );
            })}
            <Transformer ref={transformerRef} rotateEnabled keepRatio={false} />
          </Layer>
        </Stage>
      </div>
    </main>
  );
}

function LayoutSettingsPanel({
  layout,
  allShelves,
  selectedShelf,
  selectedPlacement,
  onUpdateLayout,
  onUpdateShelf,
  onUpdatePlacement,
  onUpdateStage,
  onRemovePlacement,
  onDeleteLayout,
}) {
  if (!layout) return null;

  return (
    <aside style={styles.panel}>
      <h2 style={styles.title}>Layout-Einstellungen</h2>
      <LabeledInput label="Layout-Name" value={layout.name} onChange={name => onUpdateLayout({ name })} />
      <label>
        <span style={styles.label}>Zugeordnete Begriffe</span>
        <input
          style={styles.input}
          value={layout.assignedTerms.join(', ')}
          onChange={event => onUpdateLayout({
            assignedTerms: event.target.value.split(',').map(value => value.trim().toUpperCase()).filter(Boolean),
          })}
          placeholder="z. B. IN, DB"
        />
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <input type="checkbox" checked={layout.isDefault} onChange={event => onUpdateLayout({ isDefault: event.target.checked })} />
        <span>Default-Layout für Einträge ohne Begriff</span>
      </label>
      <div style={styles.row}>
        <LabeledInput label="Stage-Breite" type="number" min={600} value={layout.stage.width} onChange={width => onUpdateStage({ width })} />
        <LabeledInput label="Stage-Höhe" type="number" min={400} value={layout.stage.height} onChange={height => onUpdateStage({ height })} />
      </div>
      <button style={styles.dangerButton} type="button" onClick={onDeleteLayout}>Layout löschen</button>

      <div style={{ marginTop: '18px' }}>
        <h3 style={{ margin: '0 0 10px', fontSize: '15px' }}>Regal</h3>
        {!selectedShelf && <p style={styles.muted}>Regal aus der Liste ziehen oder auf dem Plan anklicken.</p>}
        {selectedShelf && (
          <>
            <div style={styles.muted}>
              <span style={styles.strong}>{selectedShelf.displayName || selectedShelf.shelfKey}</span>
              <br />
              Key: {selectedShelf.shelfKey}
              {selectedShelf.areaPath ? ` | Bereich ${selectedShelf.areaPath}` : ''}
            </div>
            <LabeledInput label="Regalname / Label" value={selectedShelf.displayName} onChange={displayName => onUpdateShelf({ displayName })} />
            <label>
              <span style={styles.label}>Spaltenliste</span>
              <input
                style={styles.input}
                value={selectedShelf.columns.join(', ')}
                onChange={event => onUpdateShelf({
                  columns: event.target.value.split(',').map(value => value.trim().toUpperCase()).filter(Boolean),
                })}
              />
            </label>
            <label>
              <span style={styles.label}>Spaltenrichtung</span>
              <select style={styles.select} value={selectedShelf.columnStart} onChange={event => onUpdateShelf({ columnStart: event.target.value })}>
                <option value="left">Erster Eintrag links</option>
                <option value="right">Erster Eintrag rechts</option>
              </select>
            </label>
            <label>
              <span style={styles.label}>Ebenenliste</span>
              <input
                style={styles.input}
                value={selectedShelf.levels.join(', ')}
                onChange={event => onUpdateShelf({
                  levels: event.target.value.split(',').map(value => value.trim()).filter(Boolean),
                })}
              />
            </label>
            <label>
              <span style={styles.label}>Ebenenrichtung</span>
              <select style={styles.select} value={selectedShelf.levelStart} onChange={event => onUpdateShelf({ levelStart: event.target.value })}>
                <option value="bottom">Erster Eintrag unten</option>
                <option value="top">Erster Eintrag oben</option>
              </select>
            </label>
            <label>
              <span style={styles.label}>Notiz</span>
              <textarea
                style={mergeStyles(styles.input, { minHeight: '80px', resize: 'vertical' })}
                value={selectedShelf.note}
                onChange={event => onUpdateShelf({ note: event.target.value })}
              />
            </label>
            {selectedPlacement && (
              <>
                <div style={styles.row}>
                  <LabeledInput label="X" type="number" value={Math.round(selectedPlacement.x)} onChange={x => onUpdatePlacement(selectedShelf.shelfKey, { x })} />
                  <LabeledInput label="Y" type="number" value={Math.round(selectedPlacement.y)} onChange={y => onUpdatePlacement(selectedShelf.shelfKey, { y })} />
                </div>
                <div style={styles.row}>
                  <LabeledInput label="Breite" type="number" min={80} value={Math.round(selectedPlacement.width)} onChange={width => onUpdatePlacement(selectedShelf.shelfKey, { width })} />
                  <LabeledInput label="Höhe" type="number" min={80} value={Math.round(selectedPlacement.height)} onChange={height => onUpdatePlacement(selectedShelf.shelfKey, { height })} />
                </div>
                <LabeledInput label="Rotation" type="number" value={Math.round(selectedPlacement.rotation || 0)} onChange={rotation => onUpdatePlacement(selectedShelf.shelfKey, { rotation })} />
                <button style={styles.dangerButton} type="button" onClick={() => onRemovePlacement(selectedShelf.shelfKey)}>Platzierung entfernen</button>
              </>
            )}
          </>
        )}
      </div>

      <div style={{ marginTop: '18px' }}>
        <h3 style={{ margin: '0 0 10px', fontSize: '15px' }}>Regale im Layout</h3>
        <p style={styles.muted}>{allShelves.length} Regale verfügbar.</p>
      </div>
    </aside>
  );
}

function RackPreview({ result, shelf, layout }) {
  if (!result?.parsedLocation || !layout) {
    return (
      <div style={styles.panel}>
        <h2 style={styles.title}>Regal-Raster</h2>
        <p style={styles.muted}>Wähle einen Suchtreffer mit gültigem Lagerplatz.</p>
      </div>
    );
  }

  if (!shelf) {
    return (
      <div style={styles.panel}>
        <h2 style={styles.title}>Regal-Raster</h2>
        <p style={styles.muted}>Im Layout {layout.name} wurde noch kein passendes Regal für {result.parsedLocation.shelfKey} konfiguriert.</p>
      </div>
    );
  }

  const columns = displayColumns(shelf);
  const levels = displayLevelsTopDown(shelf);
  const hasExactSlot = Boolean(result.parsedLocation.columnCode && result.parsedLocation.levelCode);

  return (
    <div style={styles.panel}>
      <h2 style={styles.title}>Regal-Raster {shelf.displayName || shelf.shelfKey}</h2>
      <div style={styles.rackWrap}>
        <p style={styles.muted}>
          Layout {layout.name}
          {result.parsedLocation.tagNormalized ? ` | Begriff ${result.parsedLocation.tagNormalized}` : ''}
          {result.parsedLocation.areaPath ? ` | Bereich ${result.parsedLocation.areaPath}` : ''}
        </p>
        <p style={styles.muted}>Lagerplatz {result.parsedLocation.normalized}</p>
        {!hasExactSlot && (
          <p style={styles.muted}>Für diesen Eintrag wurde nur Regal/Bereich erkannt. Es gibt keine konkrete Spalte/Ebene im Feld.</p>
        )}
        <div style={styles.rackGrid}>
          {levels.map(level => (
            <div key={level} style={{ ...styles.rackRow, gridTemplateColumns: `72px repeat(${Math.max(columns.length, 1)}, minmax(0, 1fr))` }}>
              <div style={styles.rackCell}>E{level}</div>
              {columns.map(column => {
                const active = hasExactSlot && column === result.parsedLocation.columnCode && level === result.parsedLocation.levelCode;
                return <div key={`${column}-${level}`} style={active ? styles.rackCellHighlight : styles.rackCell}>{column}{level}</div>;
              })}
            </div>
          ))}
          <div style={{ ...styles.rackRow, gridTemplateColumns: `72px repeat(${Math.max(columns.length, 1)}, minmax(0, 1fr))` }}>
            <div style={styles.rackCell}>Sp.</div>
            {columns.map(column => <div key={column} style={styles.rackCell}>{column}</div>)}
          </div>
        </div>
      </div>
    </div>
  );
}

function LookupPlan({ layout, selectedShelfKey, shelfLabelMap }) {
  if (!layout) return null;

  return (
    <div style={styles.viewerShell}>
      <h2 style={styles.title}>Lagerplan {layout.name}</h2>
      <div style={styles.stageShell}>
        <Stage width={layout.stage.width} height={layout.stage.height}>
          <Layer>
            <Rect width={layout.stage.width} height={layout.stage.height} fill="#09101d" />
            {layout.placedShelves.map(placement => {
              const selected = placement.shelfKey === selectedShelfKey;
              return (
                <Group key={placement.shelfKey} x={placement.x} y={placement.y} rotation={placement.rotation || 0}>
                  <Rect
                    width={placement.width}
                    height={placement.height}
                    fill={selected ? 'rgba(34,197,94,0.24)' : 'rgba(30,41,59,0.82)'}
                    stroke={selected ? '#86efac' : 'rgba(255,255,255,0.36)'}
                    strokeWidth={selected ? 4 : 2}
                    cornerRadius={8}
                  />
                  <Text
                    text={shelfLabelMap.get(placement.shelfKey) || placement.shelfKey}
                    width={placement.width}
                    height={placement.height}
                    align="center"
                    verticalAlign="middle"
                    fill="#fff"
                    fontStyle="bold"
                    fontSize={Math.max(18, Math.min(24, placement.height * 0.22))}
                    listening={false}
                  />
                </Group>
              );
            })}
          </Layer>
        </Stage>
      </div>
    </div>
  );
}

function WarehouseLookup() {
  const [mode, setMode] = useState('setup');
  const [layouts, setLayouts] = useState([]);
  const [detectedLayouts, setDetectedLayouts] = useState([]);
  const [lookupItems, setLookupItems] = useState([]);
  const [activeLayoutId, setActiveLayoutId] = useState('');
  const [selectedShelfKey, setSelectedShelfKey] = useState('');
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupResults, setLookupResults] = useState([]);
  const [activeResultId, setActiveResultId] = useState('');
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [itemCount, setItemCount] = useState(0);
  const [draggedShelfKey, setDraggedShelfKey] = useState('');
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const lastInteractionAtRef = useRef(Date.now());
  const lastRefreshAtRef = useRef(0);
  const refreshInFlightRef = useRef(false);

  useEffect(() => {
    const applyPayload = (payload, nextStatus) => {
      setLayouts(payload.layouts);
      setDetectedLayouts(payload.detectedLayouts);
      setLookupItems(payload.items);
      setItemCount(payload.itemCount);
      setActiveLayoutId(current => current || payload.layouts[0]?.id || '');
      if (payload.cacheUpdatedAt) {
        lastRefreshAtRef.current = new Date(payload.cacheUpdatedAt).getTime() || Date.now();
      } else {
        lastRefreshAtRef.current = Date.now();
      }
      if (nextStatus) {
        setStatus(nextStatus);
      } else if (payload.syncError) {
        setStatus(payload.syncError);
      } else if (payload.cacheUpdatedAt) {
        setStatus(`Datenstand: ${new Date(payload.cacheUpdatedAt).toLocaleString('de-DE')}`);
      } else if (payload.updatedAt) {
        setStatus(`Setup geladen: ${new Date(payload.updatedAt).toLocaleString('de-DE')}`);
      } else {
        setStatus('Lager Lookup geladen.');
      }
      writeLookupCache(payload);
    };

    const loadSetup = async ({ forceRefresh = false, silent = false } = {}) => {
      if (refreshInFlightRef.current) return;
      refreshInFlightRef.current = true;
      if (forceRefresh) setRefreshing(true);
      if (!silent) setStatus(forceRefresh ? 'Aktualisiere Lagerdaten...' : 'Lade Lager Lookup...');
      try {
        const response = await apiFetch(
          forceRefresh
            ? `${API_BASE}/warehouse-lookup/refresh`
            : `${API_BASE}/warehouse-lookup/setup`
          ,
          forceRefresh ? { method: 'POST' } : undefined
        );
        const payload = normalizeSetupPayload(await response.json());
        applyPayload(payload, silent ? '' : undefined);
      } catch (error) {
        if (!silent) setStatus(forceRefresh ? 'Aktualisierung fehlgeschlagen.' : 'Lager Lookup konnte nicht geladen werden.');
      } finally {
        refreshInFlightRef.current = false;
        setRefreshing(false);
      }
    };

    const cached = readLookupCache();
    if (cached?.items?.length || cached?.layouts?.length) {
      applyPayload(cached, cached.cacheUpdatedAt ? `Cache geladen: ${new Date(cached.cacheUpdatedAt).toLocaleString('de-DE')}` : 'Cache geladen.');
    } else {
      loadSetup();
    }

    const markInteraction = () => {
      lastInteractionAtRef.current = Date.now();
    };

    const interval = window.setInterval(() => {
      const now = Date.now();
      const pageVisible = document.visibilityState === 'visible';
      const recentlyActive = now - lastInteractionAtRef.current <= ACTIVE_USAGE_WINDOW_MS;
      const stale = now - lastRefreshAtRef.current >= AUTO_REFRESH_INTERVAL_MS;
      if (pageVisible && recentlyActive && stale) {
        loadSetup({ forceRefresh: true, silent: true });
      }
    }, 60000);

    window.addEventListener('pointerdown', markInteraction);
    window.addEventListener('keydown', markInteraction);
    window.addEventListener('mousemove', markInteraction);
    window.addEventListener('touchstart', markInteraction, { passive: true });

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('pointerdown', markInteraction);
      window.removeEventListener('keydown', markInteraction);
      window.removeEventListener('mousemove', markInteraction);
      window.removeEventListener('touchstart', markInteraction);
    };
  }, []);

  useEffect(() => {
    const search = lookupQuery.trim().toLowerCase();
    const filtered = search
      ? lookupItems.filter(item => (
        String(item.article_name || '').toLowerCase().includes(search)
        || String(item.rentman_id || '').toLowerCase().includes(search)
        || String(item.storage_location || '').toLowerCase().includes(search)
      ))
      : lookupItems;
    setLookupResults(filtered);
  }, [lookupItems, lookupQuery]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!draggedShelfKey) return undefined;

    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = 'grabbing';

    return () => {
      document.body.style.cursor = previousCursor;
    };
  }, [draggedShelfKey]);

  const activeLayout = useMemo(
    () => layouts.find(layout => layout.id === activeLayoutId) || layouts[0] || null,
    [activeLayoutId, layouts]
  );

  const activeLayoutShelves = useMemo(
    () => activeLayout ? combineShelves(activeLayout, detectedLayouts) : [],
    [activeLayout, detectedLayouts]
  );

  const activeShelfMap = useMemo(() => new Map(activeLayoutShelves.map(shelf => [shelf.shelfKey, shelf])), [activeLayoutShelves]);
  const activeShelfLabelMap = useMemo(() => new Map(activeLayoutShelves.map(shelf => [shelf.shelfKey, shelf.displayName || shelf.shelfKey])), [activeLayoutShelves]);

  const selectedShelf = useMemo(
    () => activeShelfMap.get(selectedShelfKey) || null,
    [activeShelfMap, selectedShelfKey]
  );

  const selectedPlacement = useMemo(
    () => activeLayout?.placedShelves.find(placement => placement.shelfKey === selectedShelfKey) || null,
    [activeLayout, selectedShelfKey]
  );

  const activeResult = useMemo(
    () => lookupResults.find(item => String(item.id || item.rentman_id) === activeResultId) || null,
    [activeResultId, lookupResults]
  );

  useEffect(() => {
    setActiveResultId(current => (
      lookupResults.some(item => String(item.id || item.rentman_id) === current)
        ? current
        : String(lookupResults[0]?.id || lookupResults[0]?.rentman_id || '')
    ));
  }, [lookupResults]);

  const lookupLayout = useMemo(
    () => layouts.find(layout => String(layout.id) === String(activeResult?.layoutId || '')) || null,
    [activeResult, layouts]
  );

  const lookupShelfMap = useMemo(
    () => lookupLayout ? new Map(combineShelves(lookupLayout, detectedLayouts).map(shelf => [shelf.shelfKey, shelf])) : new Map(),
    [detectedLayouts, lookupLayout]
  );

  const lookupShelfLabelMap = useMemo(
    () => lookupLayout ? new Map(combineShelves(lookupLayout, detectedLayouts).map(shelf => [shelf.shelfKey, shelf.displayName || shelf.shelfKey])) : new Map(),
    [detectedLayouts, lookupLayout]
  );

  useEffect(() => {
    if (activeResult?.layoutId) setActiveLayoutId(String(activeResult.layoutId));
    if (activeResult?.parsedLocation?.shelfKey) setSelectedShelfKey(activeResult.parsedLocation.shelfKey);
  }, [activeResult]);

  const updateLayouts = updater => {
    setLayouts(current => {
      const next = updater(current).map(normalizeLayout);
      const withSingleDefault = next.map((layout, index) => ({
        ...layout,
        isDefault: layout.isDefault && !next.some((other, otherIndex) => otherIndex !== index && other.isDefault)
          ? true
          : layout.isDefault,
      }));
      if (!withSingleDefault.some(layout => layout.isDefault) && withSingleDefault[0]) {
        withSingleDefault[0] = { ...withSingleDefault[0], isDefault: true };
      }
      return withSingleDefault.map((layout, index) => ({
        ...layout,
        isDefault: layout.isDefault && withSingleDefault.findIndex(item => item.isDefault) === index,
      }));
    });
  };

  const patchActiveLayout = patch => {
    if (!activeLayout) return;
    updateLayouts(current => current.map(layout => {
      if (layout.id !== activeLayout.id) {
        return patch.isDefault ? { ...layout, isDefault: false } : layout;
      }
      return { ...layout, ...patch };
    }));
  };

  const patchActiveStage = patch => {
    if (!activeLayout) return;
    patchActiveLayout({
      stage: {
        width: Math.max(600, Number(patch.width ?? activeLayout.stage.width) || activeLayout.stage.width),
        height: Math.max(400, Number(patch.height ?? activeLayout.stage.height) || activeLayout.stage.height),
      },
    });
  };

  const patchShelf = (shelfKey, patch) => {
    if (!activeLayout) return;
    const existing = activeShelfMap.get(shelfKey) || normalizeShelf({ shelfKey, displayName: shelfKey });
    const nextShelf = normalizeShelf({ ...existing, ...patch });
    updateLayouts(current => current.map(layout => (
      layout.id !== activeLayout.id
        ? layout
        : {
            ...layout,
            shelves: [
              ...layout.shelves.filter(shelf => shelf.shelfKey !== shelfKey),
              nextShelf,
            ].sort((a, b) => a.shelfKey.localeCompare(b.shelfKey, 'de', { numeric: true })),
          }
    )));
  };

  const patchPlacement = (shelfKey, patch) => {
    if (!activeLayout) return;
    updateLayouts(current => current.map(layout => (
      layout.id !== activeLayout.id
        ? layout
        : {
            ...layout,
            placedShelves: layout.placedShelves.map(placement => (
              placement.shelfKey === shelfKey ? normalizePlacement({ ...placement, ...patch }) : placement
            )),
          }
    )));
  };

  const createPlacement = (shelfKey, position) => {
    if (!activeLayout) return;
    setSelectedShelfKey(shelfKey);
    updateLayouts(current => current.map(layout => {
      if (layout.id !== activeLayout.id) return layout;
      const existing = layout.placedShelves.find(placement => placement.shelfKey === shelfKey);
      return {
        ...layout,
        shelves: layout.shelves.some(shelf => shelf.shelfKey === shelfKey)
          ? layout.shelves
          : [...layout.shelves, normalizeShelf(activeShelfMap.get(shelfKey) || { shelfKey, displayName: shelfKey })],
        placedShelves: existing
          ? layout.placedShelves.map(placement => (
              placement.shelfKey === shelfKey ? normalizePlacement({ ...placement, ...position }) : placement
            ))
          : [...layout.placedShelves, normalizePlacement({ shelfKey, ...position })],
      };
    }));
  };

  const removePlacement = shelfKey => {
    if (!activeLayout) return;
    updateLayouts(current => current.map(layout => (
      layout.id !== activeLayout.id
        ? layout
        : { ...layout, placedShelves: layout.placedShelves.filter(placement => placement.shelfKey !== shelfKey) }
    )));
  };

  const addLayout = () => {
    const id = `layout-local-${Date.now()}`;
    const next = normalizeLayout({
      id,
      name: `Layout ${layouts.length + 1}`,
      assignedTerms: [],
      isDefault: layouts.length === 0,
      stage: DEFAULT_STAGE,
      shelves: [],
      placedShelves: [],
    }, layouts.length);
    setLayouts(current => [...current, next]);
    setActiveLayoutId(id);
    setSelectedShelfKey('');
  };

  const deleteActiveLayout = () => {
    if (!activeLayout) return;
    updateLayouts(current => {
      const remaining = current.filter(layout => layout.id !== activeLayout.id);
      return remaining.length > 0 ? remaining : [normalizeLayout({
        id: 'layout-recreated',
        name: 'Hauptlager',
        assignedTerms: [],
        isDefault: true,
        stage: DEFAULT_STAGE,
        shelves: [],
        placedShelves: [],
      })];
    });
    setActiveLayoutId(current => {
      const fallback = layouts.find(layout => layout.id !== activeLayout.id);
      return fallback?.id || '';
    });
    setSelectedShelfKey('');
  };

  const saveSetup = async () => {
    setSaving(true);
    setStatus('Speichere Lager Lookup...');
    try {
      const response = await apiFetch(`${API_BASE}/warehouse-lookup/setup`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ layouts }),
      });
      const payload = normalizeSetupPayload(await response.json());
      setLayouts(payload.layouts);
      setDetectedLayouts(payload.detectedLayouts);
      setStatus(payload.updatedAt ? `Gespeichert: ${new Date(payload.updatedAt).toLocaleString('de-DE')}` : 'Gespeichert');
      if (!payload.layouts.some(layout => layout.id === activeLayoutId)) {
        setActiveLayoutId(payload.layouts[0]?.id || '');
      }
    } catch (error) {
      setStatus('Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const refreshLookupData = async () => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    setRefreshing(true);
    setStatus('Aktualisiere Lagerdaten...');
    try {
      const response = await apiFetch(`${API_BASE}/warehouse-lookup/refresh`, { method: 'POST' });
      const payload = normalizeSetupPayload(await response.json());
      setLayouts(payload.layouts);
      setDetectedLayouts(payload.detectedLayouts);
      setLookupItems(payload.items);
      setItemCount(payload.itemCount);
      setActiveLayoutId(current => current || payload.layouts[0]?.id || '');
      lastRefreshAtRef.current = payload.cacheUpdatedAt ? (new Date(payload.cacheUpdatedAt).getTime() || Date.now()) : Date.now();
      writeLookupCache(payload);
      setStatus(payload.cacheUpdatedAt ? `Aktualisiert: ${new Date(payload.cacheUpdatedAt).toLocaleString('de-DE')}` : 'Lagerdaten aktualisiert.');
    } catch (error) {
      setStatus('Aktualisierung fehlgeschlagen.');
    } finally {
      refreshInFlightRef.current = false;
      setRefreshing(false);
    }
  };

  const isTablet = viewportWidth < 1220;
  const isMobile = viewportWidth < 760;

  return (
    <div style={styles.stack}>
      <div style={styles.tabRow}>
        <button style={mode === 'setup' ? styles.tabButtonActive : styles.tabButton} type="button" onClick={() => setMode('setup')}>Setup</button>
        <button style={mode === 'lookup' ? styles.tabButtonActive : styles.tabButton} type="button" onClick={() => setMode('lookup')}>Lookup</button>
        <div style={styles.tabActions}>
          <button style={styles.inlineButton} type="button" onClick={refreshLookupData} disabled={refreshing || saving}>
            {refreshing ? 'Aktualisiert...' : 'Aktualisieren'}
          </button>
        </div>
      </div>

      {mode === 'setup' && (
        <>
          <div style={{ ...styles.page, gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '250px minmax(0, 1fr)' : styles.page.gridTemplateColumns) }}>
            <aside style={styles.panel}>
              <h2 style={styles.title}>Layouts</h2>
              <p style={styles.muted}>
                {itemCount} Artikel aus der API im lokalen Cache.
              </p>
              {layouts.map(layout => (
                <div key={layout.id} style={activeLayout?.id === layout.id ? styles.layoutCardActive : styles.layoutCard} onClick={() => {
                  setActiveLayoutId(layout.id);
                  setSelectedShelfKey('');
                }}>
                  <strong>{layout.name}</strong>
                  <div style={styles.muted}>{layoutTermsLabel(layout)}</div>
                  {layout.isDefault && <span style={styles.badge}>Default</span>}
                </div>
              ))}
              <button style={styles.button} type="button" onClick={addLayout}>Layout hinzufügen</button>
              <button style={styles.primaryButton} type="button" onClick={saveSetup} disabled={saving}>
                {saving ? 'Speichert...' : 'Alle Layouts speichern'}
              </button>

              <div style={{ marginTop: '18px' }}>
                <h3 style={{ margin: '0 0 10px', fontSize: '15px' }}>Erkannte Regale</h3>
                {activeLayoutShelves.map(shelf => {
                  const placed = Boolean(activeLayout?.placedShelves.some(placement => placement.shelfKey === shelf.shelfKey));
                  const isDraggingShelf = draggedShelfKey === shelf.shelfKey;
                  return (
                    <div
                      key={shelf.shelfKey}
                      style={{
                        ...(selectedShelfKey === shelf.shelfKey ? styles.shelfCardActive : styles.shelfCard),
                        cursor: isDraggingShelf ? 'grabbing' : 'grab',
                        transform: isDraggingShelf ? 'translateY(-2px) scale(1.025) rotate(1.4deg)' : 'translateY(0) scale(1) rotate(0deg)',
                        boxShadow: isDraggingShelf ? '0 18px 34px rgba(0,0,0,0.32)' : 'none',
                        filter: isDraggingShelf ? 'brightness(1.06)' : 'none',
                        opacity: isDraggingShelf ? 0.78 : 1,
                      }}
                      draggable
                      onDragStart={event => {
                        setDraggedShelfKey(shelf.shelfKey);
                        event.dataTransfer.effectAllowed = 'copy';
                        event.dataTransfer.setData('text/plain', shelf.shelfKey);
                      }}
                      onDragEnd={() => setDraggedShelfKey('')}
                      onClick={() => setSelectedShelfKey(shelf.shelfKey)}
                    >
                      <strong>{shelf.displayName || shelf.shelfKey}</strong>
                      <div style={styles.muted}>
                        {shelf.areaPath ? `Bereich ${shelf.areaPath} | ` : ''}
                        {shelf.columns.join(', ') || '-'} | Ebenen {shelf.levels.join(', ') || '-'}
                      </div>
                      {shelf.articleCount > 0 && <span style={styles.badge}>{shelf.articleCount} Artikel</span>}
                      <span style={styles.badge}>{placed ? 'Platziert' : 'Nicht platziert'}</span>
                    </div>
                  );
                })}
              </div>
            </aside>

            {activeLayout && (
              <LayoutCanvas
                stage={activeLayout.stage}
                placements={activeLayout.placedShelves}
                selectedShelfKey={selectedShelfKey}
                onSelectShelf={setSelectedShelfKey}
                onUpdatePlacement={patchPlacement}
                onCreatePlacement={createPlacement}
                shelfLabelMap={activeShelfLabelMap}
              />
            )}

            {!isTablet && activeLayout && (
              <LayoutSettingsPanel
                layout={activeLayout}
                allShelves={activeLayoutShelves}
                selectedShelf={selectedShelf}
                selectedPlacement={selectedPlacement}
                onUpdateLayout={patchActiveLayout}
                onUpdateShelf={patch => selectedShelf && patchShelf(selectedShelf.shelfKey, patch)}
                onUpdatePlacement={patchPlacement}
                onUpdateStage={patchActiveStage}
                onRemovePlacement={removePlacement}
                onDeleteLayout={deleteActiveLayout}
              />
            )}
          </div>

          {mode === 'setup' && isTablet && activeLayout && (
            <LayoutSettingsPanel
              layout={activeLayout}
              allShelves={activeLayoutShelves}
              selectedShelf={selectedShelf}
              selectedPlacement={selectedPlacement}
              onUpdateLayout={patchActiveLayout}
              onUpdateShelf={patch => selectedShelf && patchShelf(selectedShelf.shelfKey, patch)}
              onUpdatePlacement={patchPlacement}
              onUpdateStage={patchActiveStage}
              onRemovePlacement={removePlacement}
              onDeleteLayout={deleteActiveLayout}
            />
          )}
        </>
      )}

      {mode === 'lookup' && (
        <div style={{ ...styles.lookupLayout, gridTemplateColumns: isMobile ? '1fr' : styles.lookupLayout.gridTemplateColumns }}>
          <aside style={styles.panel}>
            <h2 style={styles.title}>Artikelsuche</h2>
            <input
              style={styles.input}
              placeholder="Name, Rentman-ID oder Lagerplatz"
              value={lookupQuery}
              onChange={event => setLookupQuery(event.target.value)}
            />
            <p style={styles.muted}>
              Parser erkennt Begriffe wie <code>{'{In}'}</code> oder <code>[dB}</code> vor dem Lagerplatz sowie detaillierte Orte wie <code>HR1.1:R3:A2</code>.
            </p>
            <p style={styles.muted}>
              {itemCount} Artikel lokal geladen. Die API wird nur bei leerem Cache, per Klick auf Aktualisieren oder automatisch alle 5 Minuten bei aktiver Nutzung abgefragt.
            </p>
            <div style={styles.results}>
              {lookupResults.map(item => {
                const key = String(item.id || item.rentman_id);
                return (
                  <button key={key} type="button" style={activeResultId === key ? styles.resultButtonActive : styles.resultButton} onClick={() => setActiveResultId(key)}>
                    <strong>{item.article_name}</strong>
                    <div>{shelfLocationLabel(item)}</div>
                    <div style={styles.muted}>{item.layoutName || 'ohne Layout'} | {item.rentman_id || 'ohne Rentman-ID'}</div>
                  </button>
                );
              })}
              {lookupResults.length === 0 && <p style={styles.muted}>Keine passenden Artikel gefunden.</p>}
            </div>
          </aside>

          <div style={styles.stack}>
            <LookupPlan
              layout={lookupLayout}
              selectedShelfKey={activeResult?.parsedLocation?.shelfKey || ''}
              shelfLabelMap={lookupShelfLabelMap}
            />
            <RackPreview
              result={activeResult}
              shelf={lookupShelfMap.get(activeResult?.parsedLocation?.shelfKey || '') || null}
              layout={lookupLayout}
            />
          </div>
        </div>
      )}

      <div style={styles.status}>{status}</div>
    </div>
  );
}

export default WarehouseLookup;
